import { CommonModule, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { DateRange } from '@angular/material/datepicker';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { signalHelpers } from '@core';
import { add, set, startOfDay, startOfMonth, sub } from 'date-fns';
import { Subject } from 'rxjs';

import { CalendarComponent } from './calendar.component';

export const DateRangeSelect$ = new Subject<DateRange<Date>>();

interface Config {
  customRange?: boolean;
  /** Restricts the preset dropdown to these ids, in order. Defaults to the full preset set. */
  presets?: AvsDateRangeIds[];
  small?: boolean;
}

export enum AvsDateRangeIds {
  CUSTOM = -1,
  LAST_24_HOURS = 1,
  LAST_30_DAYS = 30,
  LAST_7_DAYS = 7,
  LAST_90_DAYS = 90,
  TODAY = 0,
};

const DEFAULT_PRESETS = [
  AvsDateRangeIds.TODAY,
  AvsDateRangeIds.LAST_24_HOURS,
  AvsDateRangeIds.LAST_7_DAYS,
  AvsDateRangeIds.LAST_30_DAYS,
];

const PRESET_LABELS: Record<AvsDateRangeIds, string> = {
  [AvsDateRangeIds.CUSTOM]: 'Custom',
  [AvsDateRangeIds.TODAY]: 'Today',
  [AvsDateRangeIds.LAST_24_HOURS]: 'Last 24 hours',
  [AvsDateRangeIds.LAST_7_DAYS]: 'Last 7 days',
  [AvsDateRangeIds.LAST_30_DAYS]: 'Last 30 days',
  [AvsDateRangeIds.LAST_90_DAYS]: 'Last 90 days',
};

export function getAvsDateRange(rangeId: AvsDateRangeIds) {
  const end = new Date();
  const start = add(
    rangeId === AvsDateRangeIds.TODAY ? startOfDay(end) : set(end, { seconds: 0 }),
    { days: -rangeId },
  );

  return new DateRange(start, end);
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.small]': 'config().small',
  },
  imports: [CommonModule, MatButtonModule, MatSelectModule, MatMenuModule, CalendarComponent, MatIcon, DatePipe],
  selector: 'avs-date-picker',
  styleUrl: './date-picker.component.scss',
  templateUrl: './date-picker.component.html',
})
export class DatePickerComponent {
  config = input<Config>({
    customRange: true,
    small: true,
  });

  changed = output<DateRange<Date>>();
  changedId = output<AvsDateRangeIds>();
  disabled = input(false);
  maxDate = input<Date>(new Date());
  minDate = input<Date>(startOfMonth(sub(new Date(), { months: 1 })));
  value = input<DateRange<Date> | AvsDateRangeIds>(AvsDateRangeIds.TODAY);

  protected calendar = viewChild.required(CalendarComponent);
  protected customRange = signal<DateRange<Date> | null>(null);
  protected dateRangeId = signal(AvsDateRangeIds.TODAY);
  protected eAvsDateRangeIds = AvsDateRangeIds;
  protected presetLabels = PRESET_LABELS;
  protected presetOptions = computed(() => this.config().presets ?? DEFAULT_PRESETS);
  protected previousRangeId = signal<number | null>(null);

  private range = computed(() => {
    const rangeId = this.dateRangeId();
    const range = this.customRange();

    if (rangeId === AvsDateRangeIds.CUSTOM) {
      return range!;
    }

    return getAvsDateRange(rangeId);
  });

  constructor() {
    DateRangeSelect$.pipe(takeUntilDestroyed()).subscribe(range => {
      if (this.dateRangeId() !== AvsDateRangeIds.CUSTOM) {
        this.previousRangeId.set(this.dateRangeId());
      }

      this.dateRangeId.set(AvsDateRangeIds.CUSTOM);
      this.customRange.set(range);
    });

    signalHelpers.untrackedEffect([this.value], value => {
      if (value === null) {
        return;
      }

      if (typeof value === 'number') {
        if (value !== AvsDateRangeIds.CUSTOM) {
          this.customRange.set(null);
        }
        this.dateRangeId.set(value);

        return;
      }

      if (
        value.start?.getTime() === this.range()?.start?.getTime() &&
        value.end?.getTime() === this.range()?.end?.getTime()
      ) {
        return;
      }

      if (this.dateRangeId() !== AvsDateRangeIds.CUSTOM) {
        this.previousRangeId.set(this.dateRangeId());
      }

      this.dateRangeId.set(AvsDateRangeIds.CUSTOM);
      this.customRange.set(value);
    });

    signalHelpers.untrackedEffect([
      this.dateRangeId,
      this.previousRangeId,
      this.customRange,
      this.range,
    ], (rangeId, previousRangeId, customRange, range) => {
      if (rangeId === previousRangeId || (rangeId === AvsDateRangeIds.CUSTOM && !customRange)) {
        return;
      }

      this.changed.emit(range);
      this.changedId.emit(rangeId);
    }, { debounce: 0 });
  }

  protected dateChanged(value: AvsDateRangeIds) {
    if (this.dateRangeId() !== value) {
      this.calendar().reset();
    }

    this.customRange.set(null);
    this.previousRangeId.set(this.dateRangeId());
    this.dateRangeId.set(value);
  }

  protected panelClosed() {
    if (!this.customRange()) {
      this.dateRangeId.set(this.previousRangeId()!);
    }
  }
}
